/**
 * Logger Utility
 * 
 * Production-ready logging system that replaces direct console calls.
 * Features:
 * - Environment-based log levels (development vs production)
 * - Structured logging with context
 * - Maritime-specific metadata support
 * - No infinite recursion risks
 * - Performance optimized
 * - TypeScript strict mode compatible
 * 
 * @example
 * ```typescript
 * import { Logger } from '@/lib/utils/logger';
 * 
 * const logger = new Logger('AuthContext');
 * logger.info('User logged in', { userId: '123', vessel: 'MV Example' });
 * logger.error('Login failed', { error: err, attempt: 3 });
 * ```
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

export interface LogContext {
  // Maritime-specific context
  vessel?: string;
  organizationId?: string;
  userId?: string;
  
  // Technical context
  component?: string;
  function?: string;
  
  // Additional metadata
  [key: string]: unknown;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  module: string;
  message: string;
  context?: LogContext;
  error?: Error;
}

/**
 * Logger class for structured, environment-aware logging
 */
export class Logger {
  private module: string;
  private static globalLevel: LogLevel = Logger.getEnvironmentLogLevel();
  
  constructor(module: string) {
    this.module = module;
  }
  
  /**
   * Determine log level based on environment
   */
  private static getEnvironmentLogLevel(): LogLevel {
    // Use import.meta.env instead of process.env for Vite
    const env = import.meta.env.MODE;
    const devEnv = import.meta.env.DEV;
    
    // Production: Only errors
    if (env === 'production' || !devEnv) {
      return LogLevel.ERROR;
    }
    
    // Test: Only warnings and errors
    if (env === 'test') {
      return LogLevel.WARN;
    }
    
    // Development: All logs
    return LogLevel.DEBUG;
  }
  
  /**
   * Set global log level (useful for testing or runtime adjustments)
   */
  static setLogLevel(level: LogLevel): void {
    Logger.globalLevel = level;
  }
  
  /**
   * Get current log level
   */
  static getLogLevel(): LogLevel {
    return Logger.globalLevel;
  }
  
  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= Logger.globalLevel;
  }
  
  /**
   * Format log entry for console output
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const levelStr = LogLevel[level].padEnd(5);
    const moduleStr = this.module.padEnd(20);
    
    let formatted = `[${timestamp}] ${levelStr} [${moduleStr}] ${message}`;
    
    // Add maritime context if available
    if (context) {
      const maritimeContext: string[] = [];
      if (context.vessel) maritimeContext.push(`vessel=${context.vessel}`);
      if (context.organizationId) maritimeContext.push(`org=${context.organizationId}`);
      if (context.userId) maritimeContext.push(`user=${context.userId}`);
      
      if (maritimeContext.length > 0) {
        formatted += ` [${maritimeContext.join(', ')}]`;
      }
    }
    
    return formatted;
  }
  
  /**
   * Create structured log entry (for future analytics/reporting)
   */
  private createLogEntry(level: LogLevel, message: string, context?: LogContext, error?: Error): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      module: this.module,
      message,
      context,
      error,
    };
  }
  
  /**
   * Log debug message (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const formatted = this.formatMessage(LogLevel.DEBUG, message, context);
    console.log(formatted);
    
    if (context) {
      console.log('Context:', context);
    }
  }
  
  /**
   * Log info message
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const formatted = this.formatMessage(LogLevel.INFO, message, context);
    console.log(formatted);
    
    if (context) {
      console.log('Context:', context);
    }
  }
  
  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const formatted = this.formatMessage(LogLevel.WARN, message, context);
    console.warn(formatted);
    
    if (context) {
      console.warn('Context:', context);
    }
  }
  
  /**
   * Log error message
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const formatted = this.formatMessage(LogLevel.ERROR, message, context);
    console.error(formatted);
    
    if (error) {
      if (error instanceof Error) {
        console.error('Error:', error.message);
        console.error('Stack:', error.stack);
      } else {
        console.error('Error:', error);
      }
    }
    
    if (context) {
      console.error('Context:', context);
    }
    
    // Create structured log entry for future error reporting integration
    const logEntry = this.createLogEntry(
      LogLevel.ERROR,
      message,
      context,
      error instanceof Error ? error : undefined
    );
    
    // TODO: Send to error reporting service (Sentry, etc.) in production
    // This is where we'd integrate with external error tracking
    this.sendToErrorReporting(logEntry);
  }
  
  /**
   * Placeholder for error reporting integration (Sentry, etc.)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private sendToErrorReporting(_logEntry: LogEntry): void {
    // Only in production
    if (import.meta.env.MODE !== 'production') return;
    
    // TODO: Integrate with Sentry or similar service
    // Example:
    // Sentry.captureException(_logEntry.error, {
    //   contexts: {
    //     app: {
    //       module: _logEntry.module,
    //       message: _logEntry.message,
    //     },
    //     maritime: _logEntry.context,
    //   },
    // });
  }
  
  /**
   * Log data loading operation (maritime-specific)
   */
  dataLoad(source: 'supabase' | 'dexie', entity: string, count: number, context?: LogContext): void {
    const emoji = source === 'supabase' ? '☁️' : '📦';
    this.info(`${emoji} Loaded ${count} ${entity} from ${source}`, context);
  }
  
  /**
   * Log CRUD operation (maritime-specific)
   */
  crud(operation: 'create' | 'read' | 'update' | 'delete', entity: string, id?: string, context?: LogContext): void {
    const emoji = {
      create: '➕',
      read: '👁️',
      update: '✏️',
      delete: '🗑️',
    };
    
    const message = id
      ? `${emoji[operation]} ${operation} ${entity}: ${id}`
      : `${emoji[operation]} ${operation} ${entity}`;
    
    this.info(message, context);
  }
}

/**
 * Create a module-specific logger instance
 */
export function createLogger(module: string): Logger {
  return new Logger(module);
}

/**
 * Global logger for quick access (use sparingly, prefer module-specific loggers)
 */
export const globalLogger = new Logger('Global');

/**
 * Export log level enum for testing
 */
export { LogLevel as default };
